import { getBrandDefaultLocale } from "@/lib/branding";

export type StaticUiLocale = "ru" | "kk" | "en" | "fr" | "fr-ca" | "es" | "zh";

export const DEFAULT_UI_LOCALE = "en";

function normalizeLocalePart(part: string, index: number): string {
  if (index === 0) {
    const normalized = part.toLowerCase();
    return normalized === "kz" ? "kk" : normalized;
  }

  if (part.length === 2 || part.length === 3) {
    return part.toUpperCase();
  }

  return part;
}

export function normalizeUiLocale(
  input: string | null | undefined
): string | null {
  if (!input) return null;

  const raw = input.trim().replace(/_/g, "-");
  if (!raw) return null;

  const parts = raw
    .split("-")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return null;

  return parts.map(normalizeLocalePart).join("-");
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function detectUiLocaleFromAcceptLanguage(
  acceptLanguage: string | null | undefined
): string | null {
  if (!acceptLanguage) return null;

  const parts = acceptLanguage
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  type Candidate = { locale: string; q: number; index: number };
  const candidates: Candidate[] = [];

  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    const [rangeRaw, ...paramsRaw] = part
      .split(";")
      .map((p) => p.trim())
      .filter(Boolean);

    if (!rangeRaw || rangeRaw === "*") continue;

    const locale = normalizeUiLocale(rangeRaw);
    if (!locale) continue;

    let q = 1;
    for (const param of paramsRaw) {
      const m = param.match(/^q=([0-9.]+)$/i);
      if (!m) continue;
      q = clamp01(Number(m[1]));
      break;
    }

    candidates.push({ locale, q, index: i });
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => (b.q - a.q) || (a.index - b.index));
  return candidates[0].locale;
}

export function resolveUiLocale(opts: {
  cookieLocale?: string | null;
  cookieNextLocale?: string | null;
  detectedLocale?: string | null;
  acceptLanguage?: string | null;
}): string {
  return (
    normalizeUiLocale(opts.cookieLocale) ||
    normalizeUiLocale(opts.cookieNextLocale) ||
    normalizeUiLocale(opts.detectedLocale) ||
    normalizeUiLocale(getBrandDefaultLocale()) ||
    detectUiLocaleFromAcceptLanguage(opts.acceptLanguage) ||
    DEFAULT_UI_LOCALE
  );
}

export function getStaticUiLocale(locale: string | null | undefined): StaticUiLocale {
  const normalized = normalizeUiLocale(locale);
  const base = normalized?.split("-")[0];
  const lower = normalized?.toLowerCase();

  if (base === "ru") return "ru";
  if (base === "kk") return "kk";
  if (base === "en") return "en";
  if (lower === "fr-ca") return "fr-ca";
  if (base === "fr") return "fr";
  if (base === "es") return "es";
  if (base === "zh") return "zh";

  return DEFAULT_UI_LOCALE;
}
